import React, { useEffect, useMemo, useState } from "react";
import Parse from "../parseClient";
import {
  signUpUser,
  signInUser,
  requestPasswordReset,
  signInWithApple,
  validateNameUniqueness,
} from "../utils/authService";

// FEATURE: UNIQUE_NAME_VALIDATION
const UNIQUE_NAME_VALIDATION_ENABLED = true;
// END FEATURE: UNIQUE_NAME_VALIDATION

const MODES = {
  SIGN_UP: "signup",
  SIGN_IN: "signin",
  FORGOT: "forgot",
};

// Check if running in Capacitor iOS
function isCapacitorIOS() {
  if (typeof window === "undefined" || !window.Capacitor) return false;
  return window.Capacitor.getPlatform() === 'ios';
}

export default function AuthModal({
  visible,
  initialMode = MODES.SIGN_UP,
  langString = "en",
  onClose,
  onSignInSuccess,
}) {
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showUsernameInput, setShowUsernameInput] = useState(false);
  const [usernameInput, setUsernameInput] = useState("");
  const [currentAppleUser, setCurrentAppleUser] = useState(null);
  // FEATURE: UNIQUE_NAME_VALIDATION
  const [nameValidation, setNameValidation] = useState({
    checking: false,
    available: null,
    suggestion: null,
  });
  const [usernameValidation, setUsernameValidation] = useState({
    checking: false,
    available: null,
    suggestion: null,
  });
  // END FEATURE: UNIQUE_NAME_VALIDATION

  const labels = useMemo(() => {
    if (langString === "nl") {
      return {
        title: {
          [MODES.SIGN_UP]: "Account aanmaken",
          [MODES.SIGN_IN]: "Inloggen",
          [MODES.FORGOT]: "Wachtwoord resetten",
        },
        subtitle: {
          [MODES.SIGN_UP]:
            "Maak een account aan om je scores en voortgang bij te houden.",
          [MODES.SIGN_IN]:
            "Log in om je scores en voortgang bij te houden.",
          [MODES.FORGOT]:
            "Voer je e-mailadres of naam in en we sturen je een link om je wachtwoord te herstellen.",
        },
        name: "Gebruikersnaam",
        email: "E-mailadres of naam",
        password: "Wachtwoord",
        createAccount: "Account aanmaken",
        signIn: "Inloggen",
        sendReset: "Stuur reset-link",
        working: "Bezig...",
        haveAccount: "Ik heb al een account",
        newAccount: "Nieuw account aanmaken",
        forgotPassword: "Wachtwoord vergeten?",
        backToSignIn: "Terug naar inloggen",
        closeLabel: "Sluit aanmelden",
        signupSuccess: "Account succesvol aangemaakt! Je kunt nu inloggen.",
        signinSuccess: "Succesvol ingelogd!",
        resetSuccess: "We hebben je een e-mail gestuurd om je wachtwoord te resetten.",
        nameMissing: "Vul je gebruikersnaam in.",
        emailMissing: "Vul je e-mailadres of naam in.",
        passwordMissing: "Vul je wachtwoord in.",
        emailPasswordMissing: "Vul je e-mailadres/naam en wachtwoord in.",
      };
    }
    return {
      title: {
        [MODES.SIGN_UP]: "Create Account",
        [MODES.SIGN_IN]: "Sign In",
        [MODES.FORGOT]: "Reset Password",
      },
      subtitle: {
        [MODES.SIGN_UP]:
          "Create an account to track your scores and progress.",
        [MODES.SIGN_IN]:
          "Sign in to track your scores and progress.",
          [MODES.FORGOT]:
            "Enter your email or name and we'll send you a link to reset your password.",
      },
        name: "Username",
        email: "Email or name",
        password: "Password",
      createAccount: "Create account",
      signIn: "Sign in",
      sendReset: "Send reset link",
      working: "Working...",
      haveAccount: "I have an account already",
      newAccount: "Create a new account",
      forgotPassword: "Forgot password?",
      backToSignIn: "Back to sign in",
      closeLabel: "Close sign in",
      signupSuccess: "Account created! You can now sign in.",
      signinSuccess: "Signed in successfully!",
      resetSuccess: "We sent you an email to reset your password.",
      nameMissing: "Please enter your username.",
      emailMissing: "Please enter your email or name.",
      passwordMissing: "Please enter a password.",
        emailPasswordMissing: "Please enter your email/name and password.",
    };
  }, [langString]);

  useEffect(() => {
    if (visible) {
      setMode(initialMode);
      setForm({ name: "", email: "", password: "" });
      setError("");
      setMessage("");
      setLoading(false);
      setShowUsernameInput(false);
      setUsernameInput("");
      setCurrentAppleUser(null);
      // FEATURE: UNIQUE_NAME_VALIDATION
      setNameValidation({ checking: false, available: null, suggestion: null });
      setUsernameValidation({ checking: false, available: null, suggestion: null });
      // END FEATURE: UNIQUE_NAME_VALIDATION
    }
  }, [visible, initialMode]);

  // FEATURE: UNIQUE_NAME_VALIDATION
  // Debounced name validation for signup form
  useEffect(() => {
    if (!UNIQUE_NAME_VALIDATION_ENABLED || mode !== MODES.SIGN_UP) {
      return;
    }

    const trimmedName = form.name.trim();
    
    // Skip validation if name is too short or empty
    if (trimmedName.length < 2) {
      setNameValidation({ checking: false, available: null, suggestion: null });
      return;
    }

    // Debounce timer
    const timer = setTimeout(async () => {
      setNameValidation(prev => ({ ...prev, checking: true }));
      
      try {
        const result = await validateNameUniqueness(trimmedName);
        setNameValidation({
          checking: false,
          available: result.available,
          suggestion: result.suggestion,
        });
      } catch (error) {
        console.warn("Name validation error:", error);
        setNameValidation({
          checking: false,
          available: null,
          suggestion: null,
        });
      }
    }, 600); // 600ms debounce
    
    return () => clearTimeout(timer);
  }, [form.name, mode]);

  // Debounced name validation for Apple username input
  useEffect(() => {
    if (!UNIQUE_NAME_VALIDATION_ENABLED || !showUsernameInput || !currentAppleUser) {
      return;
    }

    const trimmedName = usernameInput.trim();
    
    // Skip validation if name is too short or empty
    if (trimmedName.length < 2) {
      setUsernameValidation({ checking: false, available: null, suggestion: null });
      return;
    }

    // Debounce timer
    const timer = setTimeout(async () => {
      setUsernameValidation(prev => ({ ...prev, checking: true }));
      
      try {
        const result = await validateNameUniqueness(trimmedName, currentAppleUser.id);
        setUsernameValidation({
          checking: false,
          available: result.available,
          suggestion: result.suggestion,
        });
      } catch (error) {
        console.warn("Name validation error:", error);
        setUsernameValidation({
          checking: false,
          available: null,
          suggestion: null,
        });
      }
    }, 600); // 600ms debounce
    
    return () => clearTimeout(timer);
  }, [usernameInput, showUsernameInput, currentAppleUser]);
  // END FEATURE: UNIQUE_NAME_VALIDATION

  useEffect(() => {
    setError("");
    setMessage("");
  }, [mode]);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSwitchMode = (nextMode) => {
    setMode(nextMode);
  };

  const handleClose = () => {
    if (!loading) {
      onClose?.();
    }
  };

  const handleAppleSignIn = async () => {
    if (loading) return;
    setLoading(true);
    setError("");
    setMessage("");

    try {
      const user = await signInWithApple();
      
      // Check if user has a generic name (needs username input)
      const userName = user.get('name') || '';
      const isGenericName = !userName || 
                           userName === 'Apple User' || 
                           userName.startsWith('Apple User ') ||
                           userName === user.get('username') ||
                           userName === user.get('email');
      
      if (isGenericName && isCapacitorIOS()) {
        // Show username input for new Apple users
        setMessage(labels.signinSuccess);
        setCurrentAppleUser(user);
        setShowUsernameInput(true);
      } else {
        // User already has a name, proceed normally
        setMessage(labels.signinSuccess);
        onSignInSuccess?.(user);
        setTimeout(() => {
          handleClose();
        }, 900);
      }
    } catch (err) {
      setError(err.message || "Apple Sign In failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleUsernameSubmit = async () => {
    if (!usernameInput.trim()) {
      setError(langString === "nl" ? "Vul een gebruikersnaam in" : "Please enter a username");
      return;
    }

    if (!currentAppleUser) {
      setError("User not found");
      return;
    }

    // FEATURE: UNIQUE_NAME_VALIDATION
    if (UNIQUE_NAME_VALIDATION_ENABLED) {
      const validation = await validateNameUniqueness(usernameInput.trim(), currentAppleUser.id);
      if (!validation.available) {
        const suggestionText = validation.suggestion 
          ? (langString === "nl" 
              ? ` Deze naam is al in gebruik. Probeer "${validation.suggestion}" in plaats daarvan.`
              : ` This name is already taken. Try "${validation.suggestion}" instead.`)
          : (langString === "nl" 
              ? " Deze naam is al in gebruik."
              : " This name is already taken.");
        setError(suggestionText);
        return;
      }
    }
    // END FEATURE: UNIQUE_NAME_VALIDATION

    setLoading(true);
    setError("");

    try {
      // Update name in Parse
      currentAppleUser.set('name', usernameInput.trim());
      await currentAppleUser.save();
      
      setMessage(langString === "nl" ? "Gebruikersnaam opgeslagen!" : "Username saved!");
      onSignInSuccess?.(currentAppleUser);
      
      setTimeout(() => {
        handleClose();
      }, 900);
    } catch (err) {
      setError(err.message || "Failed to save username.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    setMessage("");

    try {
      if (mode === MODES.SIGN_UP) {
        if (!form.name.trim()) throw new Error(labels.nameMissing);
        if (!form.email.trim()) throw new Error(labels.emailMissing);
        if (!form.password) throw new Error(labels.passwordMissing);

        await signUpUser(form);
        // After signup, user is automatically logged in
        const user = Parse.User.current();
        if (user) {
          setMessage(labels.signupSuccess);
          onSignInSuccess?.(user);
          setTimeout(() => {
            handleClose();
          }, 900);
        } else {
          // Fallback: if user is not logged in, show login form
          setMessage(labels.signupSuccess);
          setMode(MODES.SIGN_IN);
          setForm({ name: "", email: form.email, password: "" });
        }
      } else if (mode === MODES.SIGN_IN) {
        if (!form.email.trim() || !form.password) {
          throw new Error(labels.emailPasswordMissing);
        }
        const user = await signInUser(form);
        setMessage(labels.signinSuccess);
        onSignInSuccess?.(user);
        setTimeout(() => {
          handleClose();
        }, 900);
      } else {
        if (!form.email.trim()) throw new Error(labels.emailMissing);
        const { email } = await requestPasswordReset(form.email);
        setMessage(labels.resetSuccess);
        setMode(MODES.SIGN_IN);
        setForm({ name: "", email, password: "" });
      }
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  if (!visible) {
    return null;
  }

  // ===== CHANGE: iOS users now use standard account creation/login (like web) =====
  // Removed iOS-only simplified modal - iOS users now see the same form as web users
  // Apple Sign-In is still available as an option below the form
  // ===== END CHANGE =====

  // Regular modal for web and other modes
  return (
    <>
      <div className="menu-overlay" onClick={(e) => {
        if (e.target === e.currentTarget) {
          e.stopPropagation();
          handleClose();
        }
      }} />
      <div className="auth-modal">
        <div className="auth-modal-frame">
          <button
            className="menu-close"
            onClick={handleClose}
            aria-label={labels.closeLabel}
          >
            ✕
          </button>
          <div className="auth-modal__content">
          <div className="auth-modal__title">{labels.title[mode]}</div>
          <div className="auth-modal__subtitle">{labels.subtitle[mode]}</div>
            {error && <div className="auth-modal__error">{error}</div>}
            {message && <div className="auth-modal__message">{message}</div>}

            <form className="auth-form" onSubmit={handleSubmit}>
              {mode === MODES.SIGN_UP && (
                <label className="auth-form__field">
                  <span>{labels.name}</span>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(event) => handleChange("name", event.target.value)}
                    disabled={loading}
                    autoComplete="given-name"
                  />
                  {/* FEATURE: UNIQUE_NAME_VALIDATION */}
                  {UNIQUE_NAME_VALIDATION_ENABLED && form.name.trim().length >= 2 && (
                    <div className="auth-form__name-validation">
                      {nameValidation.checking && (
                        <span className="auth-form__validation-checking">
                          {langString === "nl" ? "Controleren..." : "Checking..."}
                        </span>
                      )}
                      {!nameValidation.checking && nameValidation.available === false && (
                        <div className="auth-form__validation-error">
                          <span>
                            {langString === "nl" 
                              ? "Deze naam is al in gebruik." 
                              : "This name is already taken."}
                          </span>
                          {nameValidation.suggestion && (
                            <button
                              type="button"
                              className="auth-form__suggestion-button"
                              onClick={() => handleChange("name", nameValidation.suggestion)}
                            >
                              {langString === "nl" ? "Gebruik" : "Use"} "{nameValidation.suggestion}"
                            </button>
                          )}
                        </div>
                      )}
                      {!nameValidation.checking && nameValidation.available === true && (
                        <span className="auth-form__validation-success">
                          ✓ {langString === "nl" ? "Beschikbaar" : "Available"}
                        </span>
                      )}
                    </div>
                  )}
                  {/* END FEATURE: UNIQUE_NAME_VALIDATION */}
                </label>
              )}
              <label className="auth-form__field">
                <span>{labels.email}</span>
                <input
                  type={mode === MODES.SIGN_UP ? "email" : "text"}
                  value={form.email}
                  onChange={(event) => handleChange("email", event.target.value)}
                  disabled={loading}
                  autoComplete={mode === MODES.SIGN_UP ? "email" : "username"}
                  placeholder={mode === MODES.SIGN_IN ? (langString === "nl" ? "E-mailadres of naam" : "Email or name") : undefined}
                />
              </label>
              {mode !== MODES.FORGOT && (
                <label className="auth-form__field">
                  <span>{labels.password}</span>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(event) => handleChange("password", event.target.value)}
                    disabled={loading}
                    autoComplete={mode === MODES.SIGN_UP ? "new-password" : "current-password"}
                  />
                </label>
              )}
              <button 
                type="submit" 
                className="auth-form__submit" 
                disabled={
                  loading || 
                  // FEATURE: UNIQUE_NAME_VALIDATION
                  (mode === MODES.SIGN_UP && UNIQUE_NAME_VALIDATION_ENABLED && form.name.trim().length >= 2 && nameValidation.available === false)
                  // END FEATURE: UNIQUE_NAME_VALIDATION
                }
              >
                {loading ? labels.working : mode === MODES.SIGN_UP ? labels.createAccount :
                  mode === MODES.SIGN_IN ? labels.signIn : labels.sendReset}
              </button>
            </form>


            <div className="auth-modal__links">
              {mode === MODES.SIGN_IN && (
                <button type="button" className="auth-link" onClick={() => handleSwitchMode(MODES.FORGOT)}>
                  {labels.forgotPassword}
                </button>
              )}
              {mode === MODES.SIGN_IN && (
                <button type="button" className="auth-link" onClick={() => handleSwitchMode(MODES.SIGN_UP)}>
                  {labels.newAccount}
                </button>
              )}
              {mode === MODES.SIGN_UP && (
                <button type="button" className="auth-link" onClick={() => handleSwitchMode(MODES.SIGN_IN)}>
                  {labels.haveAccount}
                </button>
              )}
              {mode === MODES.FORGOT && (
                <button type="button" className="auth-link" onClick={() => handleSwitchMode(MODES.SIGN_IN)}>
                  {labels.backToSignIn}
                </button>
              )}
            </div>
            {mode === MODES.SIGN_UP && (
              <p className="auth-consent auth-consent--bottom">
                {langString === "nl"
                  ? "Door een account aan te maken ga je akkoord met onze"
                  : "By creating an account you agree to our"}{" "}
                <a
                  href="https://www.smartfriendsapp.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {langString === "nl" ? "voorwaarden" : "terms and conditions"}
                </a>
                .
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export { MODES as AuthModes };

